import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch } from 'react-redux';
import {
  Alert as MuiAlert,
  Box,
  Button,
  CardContent,
  CircularProgress,
  Divider,
  IconButton,
  Stack,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material';
import LinkIcon from '@mui/icons-material/Link';
import SaveIcon from '@mui/icons-material/Save';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import { PageLayout, PageHeader, ContentSection } from '../components/layout/PageLayout';
import { EnhancedCard } from '../components/layout/EnhancedCard';
import apiClient from '../services/apiClient';
import { showNotification } from '../store/slices/notificationSlice';
import type { AppDispatch } from '../store/store';

/**
 * Static catalogue of alert types the operator can attach an SOP URL to.
 *
 * Coupled by design: this matches the canonical `metadata.jobId` strings the
 * alert producer emits, which is also what the backend keys SOP rows by. If a
 * new alert type is introduced, add it here and on the backend enrichment path
 * — there is no enumerate-types endpoint by design (small, static, versioned
 * with the code, see plan §8).
 */
const KNOWN_ALERT_TYPES: ReadonlyArray<{ id: string; label: string }> = [
  { id: 'character-stuck-job', label: 'Character Stuck' },
  { id: 'deaths-per-hour-job', label: 'Deaths Per Hour' },
  { id: 'character-inactive-job', label: 'Character Inactive' },
  { id: 'boosting-overdue-job', label: 'Boosting Overdue' },
  { id: 'bot-reported-alerts', label: 'Bot Reported' },
  { id: 'voices-count-job', label: 'Voices Count' },
  { id: 'cohort-div-underperform', label: 'Cohort Div/Hr Underperformance' },
];

/** Mirrors `OwnerAlertSopMappingResponseDto` on the backend. */
interface SopMappingResponse {
  alertType: string;
  sopUrl: string | null;
  sopLabel: string | null;
  createdAt: string;
  updatedAt: string;
}

interface RowState {
  /** Currently committed value (last successful server state); used to detect dirty rows. */
  savedUrl: string;
  savedLabel: string;
  /** Working copy bound to the inputs. */
  url: string;
  label: string;
  /** Per-row inflight markers (separate so Save and Clear can show independently). */
  saving: boolean;
  clearing: boolean;
}

const emptyRow = (): RowState => ({
  savedUrl: '',
  savedLabel: '',
  url: '',
  label: '',
  saving: false,
  clearing: false,
});

const extractErrorMessage = (error: unknown, fallback: string): string => {
  if (typeof error === 'object' && error !== null && 'response' in error) {
    const response = (error as { response?: { data?: { message?: string | string[] } } }).response;
    const message = response?.data?.message;
    if (Array.isArray(message)) return message.join(', ');
    if (typeof message === 'string' && message.length > 0) return message;
  }
  if (error instanceof Error && error.message) return error.message;
  return fallback;
};

const AlertSopMappingsPage: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const [rows, setRows] = useState<Record<string, RowState>>(() => {
    const initial: Record<string, RowState> = {};
    for (const t of KNOWN_ALERT_TYPES) initial[t.id] = emptyRow();
    return initial;
  });
  const [loading, setLoading] = useState<boolean>(true);
  const [loadError, setLoadError] = useState<string | null>(null);

  const applyServerSnapshot = useCallback((mappings: SopMappingResponse[]) => {
    setRows(prev => {
      const next: Record<string, RowState> = {};
      // Start from current working copies so an in-flight edit isn't clobbered by a refresh.
      for (const t of KNOWN_ALERT_TYPES) {
        next[t.id] = prev[t.id] ? { ...prev[t.id] } : emptyRow();
      }
      const byType = new Map(mappings.map(m => [m.alertType, m]));
      for (const t of KNOWN_ALERT_TYPES) {
        const m = byType.get(t.id);
        const savedUrl = m?.sopUrl ?? '';
        const savedLabel = m?.sopLabel ?? '';
        const row = next[t.id];
        // Snap working copy to server values only when row is clean (not dirty, not in-flight).
        const isDirty = row.url !== row.savedUrl || row.label !== row.savedLabel;
        const inFlight = row.saving || row.clearing;
        if (!isDirty && !inFlight) {
          row.url = savedUrl;
          row.label = savedLabel;
        }
        row.savedUrl = savedUrl;
        row.savedLabel = savedLabel;
      }
      return next;
    });
  }, []);

  const fetchMappings = useCallback(async () => {
    setLoading(true);
    setLoadError(null);
    try {
      const response = await apiClient.get<SopMappingResponse[]>('/alerts/sop-mappings');
      applyServerSnapshot(response.data);
    } catch (error) {
      setLoadError(extractErrorMessage(error, 'Failed to load SOP mappings'));
    } finally {
      setLoading(false);
    }
  }, [applyServerSnapshot]);

  useEffect(() => {
    void fetchMappings();
  }, [fetchMappings]);

  const updateRow = useCallback((alertType: string, patch: Partial<RowState>) => {
    setRows(prev => ({ ...prev, [alertType]: { ...prev[alertType], ...patch } }));
  }, []);

  const handleSave = useCallback(
    async (alertType: string) => {
      const row = rows[alertType];
      if (!row || row.saving || row.clearing) return;
      // Snapshot the values we are about to commit so the optimistic write is
      // not racing against further keystrokes.
      const committingUrl = row.url;
      const committingLabel = row.label;
      updateRow(alertType, { saving: true });
      try {
        const response = await apiClient.put<SopMappingResponse>(`/alerts/sop-mappings/${encodeURIComponent(alertType)}`, {
          sopUrl: committingUrl,
          sopLabel: committingLabel,
        });
        // Apply server values authoritatively (they may have been normalized).
        updateRow(alertType, {
          saving: false,
          savedUrl: response.data.sopUrl ?? '',
          savedLabel: response.data.sopLabel ?? '',
          url: response.data.sopUrl ?? '',
          label: response.data.sopLabel ?? '',
        });
        dispatch(showNotification({ message: `Saved SOP mapping for ${alertType}`, type: 'success' }));
      } catch (error) {
        updateRow(alertType, { saving: false });
        dispatch(
          showNotification({
            message: `Failed to save SOP mapping for ${alertType}: ${extractErrorMessage(error, 'Unknown error')}`,
            type: 'error',
          }),
        );
      }
    },
    [rows, updateRow, dispatch],
  );

  const handleClear = useCallback(
    async (alertType: string) => {
      const row = rows[alertType];
      if (!row || row.saving || row.clearing) return;
      // Nothing to clear if the row was never saved and inputs are empty.
      if (row.savedUrl === '' && row.savedLabel === '' && row.url === '' && row.label === '') return;
      updateRow(alertType, { clearing: true });
      try {
        await apiClient.delete(`/alerts/sop-mappings/${encodeURIComponent(alertType)}`);
        updateRow(alertType, {
          clearing: false,
          savedUrl: '',
          savedLabel: '',
          url: '',
          label: '',
        });
        dispatch(showNotification({ message: `Cleared SOP mapping for ${alertType}`, type: 'success' }));
      } catch (error) {
        updateRow(alertType, { clearing: false });
        dispatch(
          showNotification({
            message: `Failed to clear SOP mapping for ${alertType}: ${extractErrorMessage(error, 'Unknown error')}`,
            type: 'error',
          }),
        );
      }
    },
    [rows, updateRow, dispatch],
  );

  const refreshAction = useMemo(
    () => (
      <Tooltip title='Refresh from server'>
        <span>
          <IconButton onClick={() => void fetchMappings()} disabled={loading} aria-label='Refresh SOP mappings'>
            <RefreshIcon />
          </IconButton>
        </span>
      </Tooltip>
    ),
    [fetchMappings, loading],
  );

  return (
    <PageLayout>
      <PageHeader
        icon={<LinkIcon />}
        title='SOP Mappings'
        subtitle='Attach a Standard Operating Procedure URL to each alert type. Links appear inside the expanded alert card.'
        actions={refreshAction}
      />

      <ContentSection>
        <EnhancedCard variant='outlined'>
          <CardContent sx={{ p: 3 }}>
            {loadError && (
              <MuiAlert severity='error' sx={{ mb: 2 }} onClose={() => setLoadError(null)}>
                {loadError}
              </MuiAlert>
            )}

            {loading ? (
              <Box display='flex' justifyContent='center' alignItems='center' py={6}>
                <CircularProgress />
              </Box>
            ) : (
              <Stack spacing={2} divider={<Divider flexItem />}>
                {KNOWN_ALERT_TYPES.map(({ id, label }) => {
                  const row = rows[id];
                  const isDirty = row.url !== row.savedUrl || row.label !== row.savedLabel;
                  const hasSaved = row.savedUrl !== '' || row.savedLabel !== '';
                  const trimmedUrl = row.url.trim();
                  const previewDisabled = trimmedUrl === '';
                  return (
                    <Box key={id}>
                      <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'flex-start' }}>
                        <Box sx={{ minWidth: { md: 220 }, pt: { md: 1 } }}>
                          <Typography variant='subtitle1' fontWeight={600}>
                            {label}
                          </Typography>
                          <Typography variant='caption' color='text.secondary'>
                            {id}
                          </Typography>
                        </Box>

                        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2} flex={1} sx={{ width: '100%' }}>
                          <TextField
                            label='SOP URL'
                            value={row.url}
                            onChange={e => updateRow(id, { url: e.target.value })}
                            placeholder='https://wiki.internal/runbooks/...'
                            fullWidth
                            size='small'
                            inputProps={{ 'aria-label': `${label} SOP URL` }}
                            disabled={row.saving || row.clearing}
                          />
                          <TextField
                            label='Label (optional)'
                            value={row.label}
                            onChange={e => updateRow(id, { label: e.target.value })}
                            placeholder='View SOP →'
                            size='small'
                            inputProps={{ maxLength: 128, 'aria-label': `${label} button label` }}
                            sx={{ minWidth: { sm: 220 } }}
                            disabled={row.saving || row.clearing}
                          />
                        </Stack>

                        <Stack direction='row' spacing={1} alignItems='center' sx={{ pt: { md: 0.5 } }}>
                          {previewDisabled ? (
                            <Tooltip title='Enter a URL to preview'>
                              <span>
                                <IconButton size='small' disabled aria-label={`Preview ${label} SOP link`}>
                                  <OpenInNewIcon fontSize='small' />
                                </IconButton>
                              </span>
                            </Tooltip>
                          ) : (
                            <Tooltip title='Open URL in new tab'>
                              <IconButton
                                size='small'
                                component='a'
                                href={trimmedUrl}
                                target='_blank'
                                rel='noopener noreferrer'
                                aria-label={`Preview ${label} SOP link`}
                              >
                                <OpenInNewIcon fontSize='small' />
                              </IconButton>
                            </Tooltip>
                          )}
                          <Button
                            variant='contained'
                            size='small'
                            startIcon={row.saving ? <CircularProgress size={16} color='inherit' /> : <SaveIcon />}
                            onClick={() => void handleSave(id)}
                            disabled={!isDirty || row.saving || row.clearing}
                          >
                            Save
                          </Button>
                          <Button
                            variant='outlined'
                            size='small'
                            color='error'
                            startIcon={row.clearing ? <CircularProgress size={16} color='inherit' /> : <DeleteOutlineIcon />}
                            onClick={() => void handleClear(id)}
                            disabled={(!hasSaved && row.url === '' && row.label === '') || row.saving || row.clearing}
                          >
                            Clear
                          </Button>
                        </Stack>
                      </Stack>
                    </Box>
                  );
                })}
              </Stack>
            )}
          </CardContent>
        </EnhancedCard>
      </ContentSection>
    </PageLayout>
  );
};

export default AlertSopMappingsPage;
